import { MobileTariff } from '@generated/gql/graphql';
import { useUpdatedTariffModalContext } from '@providers/basket/UpdatedTariffModalProvider/Context';
import { UpdatedTariffModalVariant } from '@src/types/bundle';

import { useSimsStore } from '../store';
import { SimSettingsFormValues } from '../types';
import { parseSimSettingsFormValues } from '../utils';

interface SubmitMobileQuote {
  simId: string;
  prevSimId: Nullable<string>;
  simInput: SimSettingsFormValues;
  onComplete: () => void;
}

const useSimSubmit = (mobileTariffs: MobileTariff[]) => {
  const { addSim, replaceSim } = useSimsStore();
  const { setModalState } = useUpdatedTariffModalContext();

  const buildSim = (simId: string, simInput: SimSettingsFormValues) => {
    const selectedTariff = mobileTariffs.find((tariff) => tariff.id === simId);
    if (!selectedTariff) {
      // Should never happen
      throw new Error('Data inconsistency. Selected tariff not found');
    }

    return {
      id: simId,
      selectedTariff,
      settings: parseSimSettingsFormValues(simInput),
    };
  };

  const handleAddSim = (
    simId: string,
    simInput: SimSettingsFormValues,
    onComplete: () => void
  ) => {
    const sim = buildSim(simId, simInput);

    setModalState(UpdatedTariffModalVariant.productAdded);
    addSim(sim);
    onComplete();
  };

  const handleEdit = (
    simId: string,
    prevSimId: string,
    simInput: SimSettingsFormValues,
    onComplete: () => void
  ) => {
    replaceSim(prevSimId, buildSim(simId, simInput));
    onComplete();
  };

  const submitSim = ({
    simId,
    prevSimId,
    simInput,
    onComplete,
  }: SubmitMobileQuote) => {
    if (!prevSimId) {
      return handleAddSim(simId, simInput, onComplete);
    }

    return handleEdit(simId, prevSimId, simInput, onComplete);
  };

  return {
    submitSim,
  };
};

export { useSimSubmit };
